<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useDevMode } from "../../composables/useDevMode";
import { useIsOffline } from "../../composables/useIsOffline";
import { useNotifications } from "../../composables/useNotifications";
import { DEFAULT_NOTIFICATION_ERROR_DESCRIPTION, DEFAULT_NOTIFICATION_ERROR_TITLE } from "../../enums";
import {
  useAccountStore,
  useAuditStore,
  useResultsStore,
  useSystemStore
} from "../../store";
import {
  captureWithPayloads,
  formatBytes,
  formatDate,
  isSameDay,
  slugify
} from "../../utils";
import SummaryCard, { SummaryCardThemes } from "../SummaryCard.vue";
import Dropdown from "../ui/Dropdown.vue";
import AuditProgressBar from "./AuditProgressBar.vue";
import DeleteModal from "./DeleteModal.vue";
import DuplicateModal from "./DuplicateModal.vue";
import NotesModal from "./NotesModal.vue";
import SaveIndicator from "./SaveIndicator.vue";
import TransferModal from "./TransferModal.vue";

const props = defineProps<{
  auditName: string;
  keyInfos: {
    title: string;
    description: string;
    value: number;
    unit?: string;
    theme?: SummaryCardThemes;
    disabled?: boolean;
  }[];
  editUniqueId: string;
}>();

const stickyIndicator = ref<HTMLDivElement>();

defineExpose({
  stickyIndicator
});

const isOffline = useIsOffline();

const router = useRouter();

const duplicateModal = ref<InstanceType<typeof DuplicateModal>>();
const deleteModal = ref<InstanceType<typeof DeleteModal>>();
const optionsDropdownRef = ref<InstanceType<typeof Dropdown>>();

const isDuplicationLoading = ref(false);

const auditStore = useAuditStore();
const resultStore = useResultsStore();
const accountStore = useAccountStore();
const notify = useNotifications();

/**
 * Duplicate audit and redirect to new audit page
 */
function confirmDuplicate(name: string) {
  isDuplicationLoading.value = true;
  auditStore
    .duplicateAudit(uniqueId.value, name)
    .then((newAuditId) => {
      isDuplicationLoading.value = false;
      duplicateModal.value?.hide();

      const [notifyTitle, notifyDescription] = accountStore.account ? [undefined, `Audit « ${name} » créé`] : [`Audit « ${name} » créé`, "Un lien d’accès à l’audit vous a été envoyé par e-mail"];
      notify("success", notifyTitle, notifyDescription, {
        action: {
          label: "Accéder à l’audit",
          cb() {
            auditStore.$reset();
            resultStore.$reset();
            router.push({ name: "audit-generation", params: { uniqueId: newAuditId } });
          }
        }
      });
    })
    .catch((error) => {
      notify(
        "error",
        "Échec de la duplication de l'audit",
        DEFAULT_NOTIFICATION_ERROR_DESCRIPTION
      );
      captureWithPayloads(error);
    });
}

const canTransferAudit = computed(() => {
  return !auditStore.currentAudit?.auditor?.isVerified
    || accountStore.account?.email === auditStore.currentAudit?.auditorEmail;
});
const transferModalRef = useTemplateRef<InstanceType<typeof DuplicateModal>>("transferModalRef");

/**
 * Transfer audit and redirect to account dashboard if connected or home
 */
async function transferAudit(newEmail: string) {
  try {
    await auditStore.transferAudit(props.editUniqueId, newEmail);

    transferModalRef.value?.hide();

    notify("success", `Audit « ${props.auditName} » transféré`, `Lien d’accès envoyé à ${newEmail}`);

    router.push({
      name: accountStore.account ? "account-dashboard" : "home"
    });
  } catch (error) {
    notify(
      "error",
      "Échec du transfert de l'audit",
      DEFAULT_NOTIFICATION_ERROR_DESCRIPTION
    );
    captureWithPayloads(error);
  }
}

/**
 * Delete audit and redirect to home page
 */
function confirmDelete() {
  const auditName = auditStore.currentAudit?.procedureName;

  auditStore
    .deleteAudit(uniqueId.value)
    .then(() => {
      resultStore.$reset();

      notify("success", undefined, `Audit « ${auditName} » supprimé`);

      if (accountStore.account) {
        router.push({ name: "account-dashboard" });
      } else {
        router.push({ name: "home" });
      }
    })
    .catch((error) => {
      notify(
        "error",
        "Échec de la supression de l'audit",
        DEFAULT_NOTIFICATION_ERROR_DESCRIPTION
      );
      captureWithPayloads(error);
    })
    .finally(() => {
      deleteModal.value?.hide();
    });
}

/**
 * Update audit notes
 */
const notesModal = ref<InstanceType<typeof NotesModal>>();
const isNotesLoading = ref(false);

function openNotesModal() {
  notesModal.value?.show();
}

const updateAuditNotes = async (notes: string) => {
  isNotesLoading.value = true;
  try {
    await auditStore.updateAuditNotes(uniqueId.value, {
      notes
    });
  } catch (error) {
    console.error(error);
    notify(
      "error",
      DEFAULT_NOTIFICATION_ERROR_TITLE,
      DEFAULT_NOTIFICATION_ERROR_DESCRIPTION
    );
    auditStore.lastRequestFailed = true;
  } finally {
    isNotesLoading.value = false;
  }
};

const route = useRoute();
const uniqueId = computed(() => route.params.uniqueId as string);

const resultsStore = useResultsStore();

const csvExportUrl = computed(
  () => `/api/audits/${uniqueId.value}/exports/csv`
);

const csvExportFilename = computed(() => {
  if (!auditStore.currentAudit?.procedureName) {
    return "audit.csv";
  }
  return `audit-${slugify(auditStore.currentAudit.procedureName)}.csv`;
});

const csvExportSizeEstimation = computed(() => {
  return 502 + Object.keys(resultsStore.data ?? {}).length * 318;
});

const isDevMode = useDevMode();

const systemStore = useSystemStore();

const showAuditProgressBar = computed(() => {
  return (
    !auditStore.currentAudit?.publicationDate ||
    (auditStore.currentAudit?.publicationDate &&
      resultsStore.auditProgress !== 1)
  );
});

const scrollSentinelRef = ref<HTMLDivElement>();
const showLeftSideBorders = ref(false);

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      const el = entries.at(0)?.target;
      showLeftSideBorders.value =
        !entries.at(0)?.isIntersecting &&
        !!el &&
        // verify that the sentinel is at the top of the screen
        el.getBoundingClientRect().top <= 64;
    },
    { rootMargin: "-64px" }
  );

  observer.observe(scrollSentinelRef.value!);
});
</script>

<template>
  <div v-if="isDevMode" class="fr-mb-4w">
    <button class="fr-btn" @click="resultsStore.DEV_fillResults(uniqueId)">
      [DEV] Remplir l’audit
    </button>
  </div>

  <h1 class="fr-mb-3v">
    Audit
    <span class="fr-sr-only">
      Ara enregistre automatiquement vos saisies. Vous serez alerté en cas de
      problème lié à l’enregistrement
    </span>
  </h1>
  <p class="fr-text--xl fr-mb-4w">{{ auditName }}</p>

  <div ref="stickyIndicator" class="fr-grid-row fr-mb-3w sticky-indicator">
    <div
      v-if="!systemStore.isOnline"
      id="offlineAlert"
      class="fr-pt-1w offline-alert"
    >
      <div class="fr-alert fr-alert--error">
        <h3 class="fr-alert__title">Tentative de connexion...</h3>
        <p>
          Vous êtes actuellement hors connexion. Veuillez vérifier votre
          connexion internet.
        </p>
      </div>
    </div>

    <div
      class="fr-col-12 fr-col-sm-5 fr-col-md-3 indicator-left-side"
      :class="{ 'with-border': showLeftSideBorders }"
    >
      <AuditProgressBar
        v-if="showAuditProgressBar"
        :value="resultStore.auditProgress"
        label="Progression de l’audit"
        tooltip-label="Informations sur la progression de l’audit"
        :size="8"
        class="progress-bar"
      >
        <template #tooltip-content>
          <p class="fr-text--sm">
            La progression de l'audit se base sur les critères évalués de chaque
            <strong>page de votre échantillon</strong>. Évaluez les critères de
            toutes les pages pour terminer votre audit.
          </p>
          <p class="fr-text--xs fr-mb-0">
            À noter : les critères des
            <strong>éléments transverses</strong> sont optionnels. Ils sont pris
            en compte dans le calcul du taux mais pas dans la progression de
            l’audit.
          </p>
        </template>
      </AuditProgressBar>

      <div
        v-else-if="auditStore.currentAudit?.publicationDate"
        class="audit-status"
      >
        <span
          class="fr-icon-success-fill fr-icon--sm audit-status-icon"
          aria-hidden="true"
        />
        <strong class="audit-status-publish">
          Terminé le
          <time :datetime="auditStore.currentAudit?.publicationDate">
            {{ formatDate(auditStore.currentAudit?.publicationDate) }}
          </time>
        </strong>
        <span v-if="auditStore.currentAudit?.editionDate && !isSameDay(auditStore.currentAudit?.publicationDate, auditStore.currentAudit?.editionDate)" class="fr-text--xs fr-mb-0 audit-status-modify">
          Mis à jour le
          <time :datetime="auditStore.currentAudit?.editionDate">
            {{ formatDate(auditStore.currentAudit?.editionDate) }}
          </time>
        </span>
      </div>
    </div>

    <div class="fr-col-12 fr-col-sm-7 fr-col-md-9 sub-header">
      <SaveIndicator
        v-if="route.name === 'audit-generation-full'"
        class="audit-main-indicator"
      />
      <ul class="fr-my-0 fr-p-0 top-actions" role="list">
        <li class="fr-p-0">
          <Dropdown
            ref="optionsDropdownRef"
            title="Actions"
            :disabled="isOffline"
          >
            <ul role="list" class="fr-p-0 fr-m-0 dropdown-list">
              <li class="fr-hidden-lg dropdown-item">
                <button
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left fr-icon-draft-line"
                  :disabled="isOffline"
                  @click="openNotesModal"
                >
                  Ajouter des observations
                </button>
              </li>
              <li aria-hidden="true" class="fr-hidden-lg dropdown-separator" />
              <li class="fr-p-0 dropdown-item">
                <component
                  :is="isOffline ? 'button' : 'RouterLink'"
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left"
                  :to="isOffline ? undefined : {
                    name: 'report',
                    params: {
                      uniqueId: auditStore.currentAudit?.consultUniqueId
                    }
                  }"
                  target="_blank"
                  :disabled="isOffline ? true : undefined"
                >
                  Consulter le rapport<span v-if="!isOffline" class="fr-sr-only">&nbsp;de l’audit {{ auditName }} (nouvelle fenêtre)</span>
                </component>
              </li>
              <li aria-hidden="true" class="dropdown-separator" />
              <li class="fr-p-0 dropdown-item">
                <RouterLink
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left fr-icon-settings-5-line"
                  :to="{
                    name: 'audit-settings',
                    params: { uniqueId: editUniqueId }
                  }"
                >
                  Modifier les paramètres<span class="fr-sr-only">&nbsp;de l’audit {{ auditName }}</span>
                </RouterLink>
              </li>
              <li class="dropdown-item">
                <button
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left fr-icon-file-copy-line"
                  @click="duplicateModal?.show()"
                >
                  Dupliquer<span class="fr-sr-only">&nbsp;l’audit {{ auditName }}</span>
                </button>
              </li>
              <li class="dropdown-item dropdown-item--with-meta">
                <button
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left fr-icon-share-forward-line fr-m-0"
                  :disabled="!canTransferAudit"
                  @click="transferModalRef?.show()"
                >
                  <span>
                    Transférer<span class="fr-sr-only">&nbsp;l’audit {{ auditName }}</span>
                  </span>
                  <span v-if="!canTransferAudit" class="fr-text--xs fr-text--regular dropdown-item-meta">
                    Seul le propriétaire peut transférer l’audit
                  </span>
                </button>
              </li>
              <li aria-hidden="true" class="dropdown-separator" />
              <li class="dropdown-item">
                <a
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left fr-icon-download-fill download-link"
                  :href="csvExportUrl"
                  :download="csvExportFilename"
                >
                  Exporter l’audit
                  <span class="fr-text--xs fr-text--regular download-meta">
                    CSV – {{ formatBytes(csvExportSizeEstimation, 2) }}
                  </span>
                </a>
              </li>
              <li aria-hidden="true" class="dropdown-separator" />
              <li class="dropdown-item">
                <button
                  class="fr-btn fr-btn--tertiary-no-outline fr-btn--icon-left fr-icon-delete-line fr-m-0 danger-button--secondary"
                  @click="deleteModal?.show()"
                >
                  Supprimer l’audit<span class="fr-sr-only">&nbsp;{{ auditName }}</span>
                </button>
              </li>
            </ul>
          </Dropdown>
        </li>

        <li class="fr-unhidden-lg fr-p-0 notes-desktop-link">
          <button
            class="fr-btn fr-btn--secondary fr-btn--icon-left fr-icon-draft-line"
            :disabled="isOffline"
            @click="openNotesModal"
          >
            Ajouter des observations
          </button>
        </li>
      </ul>
    </div>
  </div>

  <div class="metrics fr-mb-4w">
    <SummaryCard
      v-for="info in keyInfos"
      :key="info.title"
      :title="info.title"
      :description="info.description"
      :value="info.value"
      :unit="info.unit"
      :theme="info.theme"
      :disabled="info.disabled"
    />
  </div>

  <div ref="scrollSentinelRef" />

  <DuplicateModal
    :id="uniqueId"
    ref="duplicateModal"
    :original-audit-name="auditName"
    :is-loading="isDuplicationLoading"
    @confirm="confirmDuplicate"
    @closed="
      optionsDropdownRef?.buttonRef?.focus();
      optionsDropdownRef?.closeOptions();
    "
  />

  <TransferModal
    :id="editUniqueId"
    ref="transferModalRef"
    :procedure-name="auditName"
    @confirm="transferAudit"
    @closed="
      optionsDropdownRef?.buttonRef?.focus();
      optionsDropdownRef?.closeOptions();
    "
  />

  <DeleteModal
    v-if="auditStore.currentAudit"
    :id="uniqueId"
    ref="deleteModal"
    :procedure-name="auditStore.currentAudit.procedureName"
    @confirm="confirmDelete"
    @closed="
      optionsDropdownRef?.buttonRef?.focus();
      optionsDropdownRef?.closeOptions();
    "
  />

  <NotesModal
    ref="notesModal"
    :is-loading="isNotesLoading"
    @confirm="updateAuditNotes"
  />
</template>

<style scoped>
.sub-header {
  display: flex;
  column-gap: 1rem;
  align-items: center;
  justify-content: space-between;
  z-index: 3;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  list-style: none;
}

.download-link {
  display: flex;
  flex-wrap: wrap;
}

.download-meta {
  flex-basis: 100%;
  color: var(--text-mention-grey);
}

.notes-desktop-link {
  display: none;
}

.offline-alert {
  z-index: 4;
  width: 100%;
}

.sticky-indicator {
  position: sticky;
  top: 0;
  z-index: 4;
  gap: 0.5rem 0;
  align-items: center;
  background: var(--background-default-grey);
  min-height: 4rem;
}

.audit-main-indicator {
  margin-left: 0.75rem;
}

@media (width < 36rem) {
  .audit-main-indicator {
    margin-left: 0;
  }
}

.audit-status {
  display: grid;
  grid-template-areas:
    "icon publish"
    "empty modify";
  gap: 0 0.375rem;
  align-items: first baseline;
  grid-template-columns: 1.25rem auto;
}

.audit-status-icon {
  color: var(--text-default-success);
  grid-area: icon;

  &::after,
  &::before {
    --icon-size: 1.25rem;
  }
}

.audit-status-publish {
  grid-area: publish;
}

.audit-status-modify {
  grid-area: modify;
}

.indicator-left-side {
  display: flex;
  align-items: center;
  align-self: stretch;
  border-bottom: 1px solid transparent;
  border-right: 1px solid var(--border-default-grey);
  transition: border-color 0.2s ease;
  padding-right: 0.75rem;
}

@media (width < 48rem) {
  .indicator-left-side {
    padding-right: 1rem;
  }
}

@media (width < 36rem) {
  .indicator-left-side {
    border-right: none;
    padding-right: 0;
  }
}

.indicator-left-side.with-border {
  border-color: var(--border-default-grey);
}

.progress-bar {
  flex-grow: 1;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(v-bind("keyInfos.length"), 1fr);
  gap: 0 1rem;

  @media (width < 62rem) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
